/**
 * --------------------------------------------------------------------------
 * Basic Modules: StickyHeader.js
 * --------------------------------------------------------------------------
 */

import Util from "./util";

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const DEBUG             = false;
const NAME              = 'stickyHeader';
const DATA_KEY          = NAME;
const EVENT_KEY         = `.${DATA_KEY}`;
const LOG_KEY           = `[${NAME}]`;

const Default = {
    stickyPosXs         : null,
    stickyPosSm         : null,
    stickyPosMd         : null,
    stickyPosLg         : null,
    animate             : false,
    cssStickyHeaderOn   : 'c:header-sticky-on',
};

const Event = {
    RESIZE              : `resize${EVENT_KEY} orientationchange${EVENT_KEY}`,
    SCROLL              : `scroll${EVENT_KEY}`,
    CLICK               : `click${EVENT_KEY}`,
};

const ClassName = {
    MOBILE_MENU_ACTIVE  : 'c:mobile-header-navigation-open',
};

const Selector = {
    TOGGLE              : '[data-toggle="sticky-header"]',
    HEADER_TOP          : '[data-header-top]',
    HEADER_WRAPPER      : '[data-header-wrapper]',
};

class StickyHeader {

    constructor(element, config) {

        DEBUG && console.log(LOG_KEY, 'Init');

        if (Util.isEditMode()) {
            return;
        }

        this.resizeTimeout = null;

        this._element = element;
        this._config  = this._getConfig(config);

        this._headerTop = document.querySelector(Selector.HEADER_TOP);
        this._headerWrapper = document.querySelector(Selector.HEADER_WRAPPER);
        this.headerHeight = this._calcHeaderHeight();
        this.headerTopHeight = this._headerTop ? this._calcHeaderTopHeight() : 0;

        this._addEventListeners();
    }

    destroy() {
        $(this._element).off(EVENT_KEY);
        $.removeData(this._element, DATA_KEY);
        this._element = null;
    }

    // Private

    _calcHeaderHeight() {
        return $(this._element).outerHeight();
    }

    _calcHeaderTopHeight() {
        return this._headerTop ? $(this._headerTop).outerHeight() : 0;
    }

    _getConfig(config) {
        return {
            ...Default,
            ...config
        };
    }

    _addEventListeners() {
        $(window).on(Event.RESIZE, (event) => this._updateHeader(event));
        $(window).on(Event.SCROLL, (event) => this._handleScroll(event));
    }

    _handleScroll() {

        // Do nothing if the header is hidden - e.g. via the stylesheet for responsive purposes.
        if ($(this._element).is(':hidden')) {
            this._stickyHeaderOff();
            return;
        }

        let $this = $(window),
            scrollTop = $this.scrollTop(),
            width = $this.width(),
            scrollPoint = (null !== this._getScrollPoint()) ? this._getScrollPoint() : (this.headerTopHeight + this.headerHeight);

        if (scrollTop > scrollPoint && width > 0) {
            this._stickyHeaderOn();
        }
        else {
            this._stickyHeaderOff();
        }
    }

    _isStickyHeaderOn() {
        return document.body.classList.contains(this._config.cssStickyHeaderOn);
    }

    _stickyHeaderOff() {
        if (this._isStickyHeaderOn()) {
            document.body.classList.remove(this._config.cssStickyHeaderOn);
            $(this._element).removeClass('x:animated x:menu-fadeInDown');
            $(this._headerWrapper).css('paddingBottom', 0);
        }
    }

    _stickyHeaderOn() {

        if (! this._isStickyHeaderOn()) {
            document.body.classList.add(this._config.cssStickyHeaderOn);

            if (this._config.animate) {
                $(this._element).addClass('x:animated x:menu-fadeInDown');
            }

            $(this._headerWrapper).css('paddingBottom', `${this.headerHeight}px`);
        }
    }

    _updateHeader(event) {

        let $this = $(event.currentTarget),
            scrollPoint;

        scrollPoint = (null !== this._getScrollPoint()) ? this._getScrollPoint() : (this.headerTopHeight + this.headerHeight);
        scrollPoint = scrollPoint - 5;

        this.headerHeight = this._calcHeaderHeight();
        this.headerTopHeight = this._calcHeaderTopHeight();

        if ($this.scrollTop() < scrollPoint) {
            this.headerHeight = this._calcHeaderHeight();
            this.headerTopHeight = this._calcHeaderHeight();
        }
    }

    _buildScrollPoints() {

        let scrollPoints = {};

        if (null !== this._config.stickyPosXs) {
            scrollPoints.xs = this._config.stickyPosXs;
        }

        if (null !== this._config.stickyPosSm) {
            scrollPoints.sm = this._config.stickyPosSm;
        }

        if (null !== this._config.stickyPosMd) {
            scrollPoints.md = this._config.stickyPosMd;
        }

        if (null !== this._config.stickyPosLg) {
            scrollPoints.lg = this._config.stickyPosLg;
        }

        return scrollPoints;
    }

    _getScrollPoint() {
        let breakPoints = Util.breakPoints,
            scrollPoints = this._buildScrollPoints(),
            currentBreakPoint = Util.currentBreakPoint,
            scrollPoint,
            value = 0;

        for (let breakPoint in breakPoints) {
            if (! breakPoints.hasOwnProperty(breakPoint)) continue;

            if (scrollPoints.hasOwnProperty(breakPoint)) {
                value = scrollPoints[breakPoint];
            }

            scrollPoint = value;

            if (breakPoint === currentBreakPoint) {
                break;
            }
        }

        return scrollPoint;
    }


    // Static

    /**
     * Convenient static method to initiate the module.
     *
     * @param config
     * @param selector
     */
    static bind (config, selector) {
        StickyHeader._jQueryInterface.call($(selector || Selector.TOGGLE), config);
    }

    /**
     * jQuery Interface
     * @param config
     * @returns {*}
     */
    static _jQueryInterface(config) {
        return this.each(function() {
            const $this = $(this);
            let data    = $this.data(DATA_KEY);
            let _config = {
                ...Default,
                ...$this.data(),
                ...typeof config === 'object' && config ? config : {}
            };

            if (!data) {
                data = new StickyHeader(this, _config);
                $this.data(DATA_KEY, data);
            }

            if (typeof config === 'string') {
                if (typeof data[config] === 'undefined') {
                    throw new TypeError(`No method named "${config}"`);
                }
                data[config]();
            }
        });
    }
}

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = StickyHeader._jQueryInterface;
$.fn[NAME].Constructor = StickyHeader;


export default StickyHeader;
